import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrapper } from './style';
import OrderItems from './lib/order-items';
import Products from './lib/products';
import { MakeApiCall } from "../../../../apis";
import { ConvertParams } from "../../../../config";
import { message, notification, Spin, Checkbox, Dropdown, Button, Select, Input, InputNumber, Modal, Divider, Popconfirm } from "antd";
import { SettingOutlined, FilterOutlined, PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';

// ─── Operator display labels ──────────────────────────────────────────────────

const OPERATOR_LABELS = {
    gte:      '≥  Greater than or equal to',
    lte:      '≤  Less than or equal to',
    eq:       '=  Equals',
    contains: 'Contains',
};

const OPERATOR_SHORT = {
    gt: '>', lt: '<', gte: '≥', lte: '≤', eq: '=', contains: 'contains',
};

// ─── Map API operator symbols to internal operator keys ───────────────────────

const API_OPERATOR_MAP = {
    '>':        'gt',
    '<':        'lt',
    '>=':       'gte',
    '<=':       'lte',
    '=':        'eq',
    'contains': 'contains',
};

// ─── Map internal operator keys back to API operator symbols ─────────────────

const INTERNAL_OPERATOR_MAP = {
    'gt':       '>',
    'lt':       '<',
    'gte':      '>=',
    'lte':      '<=',
    'eq':       '=',
    'contains': 'contains',
};

// ─── Map dropdown column keys → filter_dropdown_options API keys ──────────────

const DROPDOWN_OPTIONS_KEY_MAP = {
    listing_status:   'product_listing_status',
    fulfillment_type: 'fulfillment_type',
    tags:             'tags',
};

// ─── FilterPanel component ────────────────────────────────────────────────────

const FilterPanel = ({ filterControls, filterDropdownOptions: directDropdownOptions = {}, presets, selectedPresetKeys, onPresetClick, presetsLoading, onSavePreset, onUpdatePreset, onDeletePreset }) => {
    const [open, setOpen] = useState(false);
    const [newCol, setNewCol] = useState(null);
    const [newOp,  setNewOp]  = useState(null);
    const [newVal, setNewVal] = useState('');

    // ── Save-as-preset state ───────────────────────────────────────────────
    const [saveMode,       setSaveMode]       = useState(false);
    const [savePresetName, setSavePresetName] = useState('');
    const [savingPreset,   setSavingPreset]   = useState(false);

    // ── Rename (update) state ──────────────────────────────────────────────
    const [editingPresetId,   setEditingPresetId]   = useState(null);
    const [editingPresetName, setEditingPresetName] = useState('');

    // ── Delete loading state ───────────────────────────────────────────────
    const [deletingId, setDeletingId] = useState(null);

    const handleSaveClick = async () => {
        if (!savePresetName.trim() || savingPreset) return;
        setSavingPreset(true);
        await onSavePreset(savePresetName.trim());
        setSavingPreset(false);
        setSaveMode(false);
        setSavePresetName('');
    };

    const handleRenameConfirm = async (id) => {
        if (!editingPresetName.trim()) return;
        await onUpdatePreset(id, editingPresetName.trim());
        setEditingPresetId(null);
    };

    if (!filterControls) return null;

    const { filterableColumns, activeFilters, addFilter, updateFilter, removeFilter, clearFilters, filterDropdownOptions: controlsDropdownOptions } = filterControls;

    // Merge options from both sources: direct prop (ItemMaster/fetchPresets) wins over
    // what Products passes via filterControls (products-API path, can be empty on cache hit).
    const filterDropdownOptions = Object.keys(directDropdownOptions || {}).length > 0
        ? directDropdownOptions
        : (controlsDropdownOptions || {});

    const existingFilter = activeFilters.find(f => f.column === newCol);
    const isEditMode     = !!existingFilter;
    const colDef         = filterableColumns.find(c => c.key === newCol);

    // For preset columns not in FILTERABLE_COLUMNS, fall back to the stored filter's type/label
    const effectiveType  = colDef?.type  || existingFilter?.type  || 'numeric';
    const effectiveLabel = colDef?.label || existingFilter?.columnLabel || newCol;

    const operatorOptions = effectiveType === 'numeric'
        ? [
            { value: 'gt',  label: '>  Greater than'             },
            { value: 'lt',  label: '<  Less than'                },
            { value: 'gte', label: '≥  Greater than or equal to' },
            { value: 'lte', label: '≤  Less than or equal to'    },
            { value: 'eq',  label: '=  Equals'                   },
        ]
        : effectiveType === 'dropdown'
            ? [{ value: 'eq', label: '=  Equals' }]
            : [
                { value: 'contains', label: 'Contains'       },
                { value: 'eq',       label: 'Equals (exact)' },
            ];

    // For dropdown columns, resolve the list of selectable values from the API options
    const dropdownOptionsKey     = DROPDOWN_OPTIONS_KEY_MAP[newCol];
    const dropdownValueOptions   = dropdownOptionsKey && filterDropdownOptions
        ? (filterDropdownOptions[dropdownOptionsKey] || []).map(v => ({ value: v, label: v }))
        : [];

    const handleColChange = val => {
        const existing = activeFilters.find(f => f.column === val);
        setNewCol(val);
        if (existing) {
            setNewOp(existing.operator);
            setNewVal(String(existing.value));
        } else {
            const def = filterableColumns.find(c => c.key === val);
            // Auto-select 'eq' for dropdown columns — no condition step needed
            setNewOp(def?.type === 'dropdown' ? 'eq' : null);
            setNewVal('');
        }
    };

    const handleSubmit = () => {
        if (!newCol || !newOp || newVal === '' || newVal === null) return;
        const value = effectiveType === 'numeric' ? parseFloat(newVal) : newVal;
        if (isEditMode) {
            updateFilter(existingFilter.id, { operator: newOp, value });
        } else {
            addFilter({
                id:          `${newCol}_${Date.now()}`,
                column:      newCol,
                columnLabel: effectiveLabel,
                type:        effectiveType,
                operator:    newOp,
                value,
            });
        }
        setNewCol(null);
        setNewOp(null);
        setNewVal('');
    };

    const handleClose = () => {
        setOpen(false);
        setNewCol(null);
        setNewOp(null);
        setNewVal('');
    };

    const canSubmit = newCol && newOp && newVal !== '' && newVal !== null;
    const usedColumnKeys = new Set(activeFilters.map(f => f.column));

    return (
        <>
            <button
                className={`btn fs-7 fw-bold d-flex align-items-center gap-2 px-3 px-md-5 ${activeFilters.length > 0 ? 'btn-danger' : 'btn-danger'}`}
                onClick={() => setOpen(true)}
            >
                <FilterOutlined />
                Filters
                {activeFilters.length > 0 && (
                    <span className="badge rounded-pill bg-white text-danger" style={{ fontSize: 10, minWidth: 18, padding: '1px 6px', lineHeight: '14px' }}>
                        {activeFilters.length}
                    </span>
                )}
            </button>

            <Modal
                open={open}
                onCancel={handleClose}
                title={
                    <div className="d-flex align-items-center gap-2">
                        <FilterOutlined style={{ color: '#dc3545' }} />
                        <span className="fw-bold">Column Filters</span>
                        {activeFilters.length > 0 && (
                            <span className="badge bg-danger ms-1 text-light" style={{ fontSize: 11 }}>{activeFilters.length} Active</span>
                        )}
                    </div>
                }
                footer={
                    <div className="d-flex align-items-center justify-content-between">
                        <span style={{ fontSize: 11, color: '#aaa' }} />
                        <div className="d-flex gap-2">
                            {activeFilters.length > 0 && (
                                <button className="btn btn-sm btn-outline-danger" onClick={clearFilters}>Clear all filters</button>
                            )}
                            <button className="btn btn-sm btn-danger" onClick={handleClose}>Done</button>
                        </div>
                    </div>
                }
                width={560}
                styles={{ body: { padding: '8px 0', maxHeight: '70vh', overflowY: 'auto' } }}
                destroyOnClose={false}
            >
                {/* ─── Saved Filters (Presets) ────────────────────────────── */}
                {(presets?.length > 0 || presetsLoading) && (
                    <div style={{ padding: '12px 24px 8px' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999', marginBottom: 10 }}>
                            Saved Filters
                        </div>

                        {/* System Presets */}
                        {presets.filter(p => p.type === 'system').length > 0 && (
                            <>
                                <div style={{ fontSize: 11, color: '#bbb', marginBottom: 6, fontWeight: 500 }}>System</div>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                    {presets.filter(p => p.type === 'system').map(preset => {
                                        const isActive = selectedPresetKeys.has(preset.key);
                                        return (
                                            <button key={preset.key} className="btn btn-sm fw-semibold d-flex align-items-center gap-1"
                                                style={{ borderRadius: 20, padding: '5px 16px', fontSize: 12, transition: 'all 0.2s',
                                                    border: isActive ? '1.5px solid #314F71' : '1.5px solid #dee2e6',
                                                    background: isActive ? '#314F71' : '#fff',
                                                    color: isActive ? '#fff' : '#555',
                                                    boxShadow: isActive ? '0 2px 6px rgba(49,79,113,0.18)' : 'none',
                                                }}
                                                onClick={() => onPresetClick(preset)}>
                                                {isActive && <span style={{ fontSize: 11, lineHeight: 1 }}>✓</span>}
                                                {preset.label}
                                                {isActive && <span style={{ marginLeft: 2, fontSize: 10, lineHeight: 1, opacity: 0.85 }}>✕</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Custom Presets */}
                        {presets.filter(p => p.type === 'custom').length > 0 && (
                            <>
                                <div style={{ fontSize: 11, color: '#bbb', marginBottom: 6, fontWeight: 500 }}>Custom</div>
                                <div className="d-flex flex-wrap gap-2">
                                    {presets.filter(p => p.type === 'custom').map(preset => {
                                        const isActive  = selectedPresetKeys.has(preset.key);
                                        const isEditing = editingPresetId === preset.id;
                                        const isDeleting = deletingId === preset.id;

                                        if (isEditing) {
                                            return (
                                                <div key={preset.key} className="d-flex align-items-center gap-1"
                                                    style={{ border: '1.5px solid #314F71', borderRadius: 20, padding: '3px 10px', background: '#f0f4ff' }}>
                                                    <input autoFocus
                                                        value={editingPresetName}
                                                        onChange={e => setEditingPresetName(e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') handleRenameConfirm(preset.id);
                                                            if (e.key === 'Escape') setEditingPresetId(null);
                                                        }}
                                                        style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 12, fontWeight: 600, minWidth: 80, maxWidth: 160 }}
                                                    />
                                                    <button className="btn p-0" style={{ lineHeight: 1, color: '#314F71' }} onClick={() => handleRenameConfirm(preset.id)}>
                                                        <i className="bi bi-check-lg" style={{ fontSize: 13 }} />
                                                    </button>
                                                    <button className="btn p-0" style={{ lineHeight: 1, color: '#aaa' }} onClick={() => setEditingPresetId(null)}>
                                                        <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                                                    </button>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div key={preset.key} className="d-flex align-items-center"
                                                style={{ borderRadius: 20, overflow: 'hidden',
                                                    border: isActive ? '1.5px solid #314F71' : '1.5px solid #dee2e6',
                                                    background: isActive ? '#314F71' : '#fff',
                                                    boxShadow: isActive ? '0 2px 6px rgba(49,79,113,0.18)' : 'none',
                                                    transition: 'all 0.2s',
                                                }}>
                                                <button className="btn btn-sm fw-semibold d-flex align-items-center gap-1"
                                                    style={{ borderRadius: 0, padding: '5px 10px 5px 14px', fontSize: 12, border: 'none',
                                                        background: 'transparent', color: isActive ? '#fff' : '#555', transition: 'all 0.2s',
                                                    }}
                                                    onClick={() => onPresetClick(preset)}>
                                                    {isActive && <span style={{ fontSize: 11, lineHeight: 1 }}>✓</span>}
                                                    {preset.label}
                                                    {isActive && <span style={{ marginLeft: 2, fontSize: 10, lineHeight: 1, opacity: 0.85 }}>✕</span>}
                                                </button>
                                                <div className="d-flex align-items-center" style={{ paddingRight: 8, gap: 4, borderLeft: isActive ? '1px solid rgba(255,255,255,0.2)' : '1px solid #f0f0f0', paddingLeft: 6 }}>
                                                    <button className="btn p-0" title="Rename"
                                                        style={{ lineHeight: 1, color: isActive ? 'rgba(255,255,255,0.75)' : '#adb5bd', fontSize: 11 }}
                                                        onClick={e => { e.stopPropagation(); setEditingPresetId(preset.id); setEditingPresetName(preset.label); }}>
                                                        <EditOutlined />
                                                    </button>
                                                    <Popconfirm
                                                        title="Delete this preset?"
                                                        description="This action cannot be undone."
                                                        onConfirm={async () => {
                                                            setDeletingId(preset.id);
                                                            await onDeletePreset(preset.id, preset.key);
                                                            setDeletingId(null);
                                                        }}
                                                        okText="Delete" cancelText="Cancel"
                                                        okButtonProps={{ danger: true, size: 'small' }}
                                                        cancelButtonProps={{ size: 'small' }}
                                                    >
                                                        <button className="btn p-0" title="Delete"
                                                            style={{ lineHeight: 1, color: isActive ? 'rgba(255,255,255,0.75)' : '#fca5a5', fontSize: 11 }}
                                                            disabled={isDeleting}
                                                            onClick={e => e.stopPropagation()}>
                                                            {isDeleting ? <Spin size="small" /> : <DeleteOutlined />}
                                                        </button>
                                                    </Popconfirm>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {presetsLoading && <Spin size="small" />}
                        <Divider style={{ margin: '12px 0 0' }} />
                    </div>
                )}

                {activeFilters.length > 0 && (
                    <div style={{ padding: '0 24px 16px' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999', marginBottom: 10 }}>
                            Applied Filters
                        </div>
                        <div className="d-flex flex-column gap-2">
                            {activeFilters.map(f => {
                                const isBeingEdited = newCol === f.column;
                                return (
                                    <div key={f.id} className="d-flex align-items-center justify-content-between rounded-2 px-3 py-2"
                                         style={{ background: isBeingEdited ? '#fff3cd' : '#fff4f4', border: isBeingEdited ? '1px solid #ffc107' : '1px solid #ffd6d6', transition: 'background 0.2s, border-color 0.2s' }}>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="fw-semibold" style={{ fontSize: 13, color: '#1e1e2d' }}>{f.columnLabel}</span>
                                            <span style={{ fontSize: 11, background: '#e9ecef', color: '#495057', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{OPERATOR_SHORT[f.operator]}</span>
                                            <span className="fw-bold" style={{ fontSize: 13, color: '#dc3545' }}>{String(f.value)}</span>
                                            {isBeingEdited && (
                                                <span style={{ fontSize: 10, background: '#ffc107', color: '#000', padding: '1px 7px', borderRadius: 3, fontWeight: 600 }}>editing</span>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <button className="btn p-0" style={{ color: isBeingEdited ? '#ffc107' : '#adb5bd', lineHeight: 1, fontSize: 13 }} onClick={() => handleColChange(f.column)} title="Edit this filter">
                                                <i className="bi bi-pencil" />
                                            </button>
                                            <button className="btn p-0" style={{ color: '#ccc', lineHeight: 1 }}
                                                    onClick={() => {
                                                        removeFilter(f.id);
                                                        if (isBeingEdited) { setNewCol(null); setNewOp(null); setNewVal(''); }
                                                    }} title="Remove this filter">
                                                <DeleteOutlined style={{ fontSize: 14 }} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Divider style={{ margin: '16px 0 0' }} />
                    </div>
                )}

                {/* ─── Save current filters as Preset ─────────────────────── */}
                {activeFilters.length > 0 && (
                    <div style={{ padding: '10px 24px 4px' }}>
                        {!saveMode ? (
                            <button className="btn btn-sm d-flex align-items-center gap-1"
                                style={{ color: '#314F71', fontWeight: 600, fontSize: 12, padding: '2px 0' }}
                                onClick={() => setSaveMode(true)}>
                                <SaveOutlined style={{ fontSize: 12 }} />
                                Save current filters as preset
                            </button>
                        ) : (
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    autoFocus
                                    size="middle"
                                    placeholder="Preset name..."
                                    value={savePresetName}
                                    onChange={e => setSavePresetName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && savePresetName.trim()) handleSaveClick();
                                        if (e.key === 'Escape') { setSaveMode(false); setSavePresetName(''); }
                                    }}
                                    style={{ width: 200, borderRadius: 8 }}
                                />
                                <button className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                                    style={{ fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', height: 32 }}
                                    disabled={!savePresetName.trim() || savingPreset}
                                    onClick={handleSaveClick}>
                                    {savingPreset ? <Spin size="small" /> : <><SaveOutlined style={{ fontSize: 11 }} /> Save</>}
                                </button>
                                <button className="btn btn-sm btn-link text-muted p-0" style={{ fontSize: 12, height: 32 }}
                                    onClick={() => { setSaveMode(false); setSavePresetName(''); }}>
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div style={{ padding: '16px 24px 8px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14, color: isEditMode ? '#856404' : '#999' }}>
                        {isEditMode ? `✎  Editing filter: ${effectiveLabel}` : activeFilters.length > 0 ? 'Add Another Filter' : 'Add Filter'}
                    </div>

                    <div className="mb-3">
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 6 }}>1. Select Column</div>
                        <Select
                            placeholder="Choose a column to filter..."
                            style={{ width: '100%' }}
                            size="large"
                            value={newCol}
                            onChange={handleColChange}
                            showSearch
                            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                            options={filterableColumns.map(c => ({ value: c.key, label: c.label, disabled: false }))}
                            optionRender={option => (
                                <div className="d-flex align-items-center justify-content-between">
                                    <span>{option.label}</span>
                                    {usedColumnKeys.has(option.value) && (
                                        <span style={{ fontSize: 10, background: option.value === newCol ? '#ffc107' : '#e9ecef', color: option.value === newCol ? '#000' : '#888', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>
                                            {option.value === newCol ? 'editing' : 'filtered'}
                                        </span>
                                    )}
                                </div>
                            )}
                        />
                    </div>

                    {newCol && effectiveType !== 'dropdown' && (
                        <div className="mb-3">
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 6 }}>
                                2. Select Condition
                                <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400, marginLeft: 6 }}>({effectiveType === 'numeric' ? 'numeric' : 'text'})</span>
                            </div>
                            <Select
                                placeholder="Choose a condition..."
                                style={{ width: '100%' }}
                                size="large"
                                value={newOp}
                                onChange={val => { setNewOp(val); if (!isEditMode) setNewVal(''); }}
                                options={operatorOptions}
                            />
                        </div>
                    )}

                    {newCol && newOp && (
                        <div className="mb-4">
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 6 }}>
                                {effectiveType === 'dropdown' ? '2. Select Value' : '3. Enter Value'}
                            </div>
                            {effectiveType === 'dropdown' ? (
                                <Select
                                    placeholder="Select a value..."
                                    style={{ width: '100%' }}
                                    size="large"
                                    value={newVal === '' ? undefined : newVal}
                                    onChange={val => setNewVal(val ?? '')}
                                    options={dropdownValueOptions}
                                    notFoundContent={
                                        dropdownValueOptions.length === 0
                                            ? <span style={{ color: '#aaa', fontSize: 12 }}>No options available</span>
                                            : null
                                    }
                                />
                            ) : effectiveType === 'numeric' ? (
                                <InputNumber
                                    placeholder="Enter a number..."
                                    style={{ width: '100%' }}
                                    size="large"
                                    value={newVal === '' ? null : newVal}
                                    onChange={val => setNewVal(val ?? '')}
                                    controls={false}
                                />
                            ) : (
                                <Input
                                    placeholder="Enter text..."
                                    size="large"
                                    value={newVal}
                                    onChange={e => setNewVal(e.target.value)}
                                    onPressEnter={() => { if (canSubmit) handleSubmit(); }}
                                />
                            )}
                        </div>
                    )}

                    <button
                        className={`fs-7 btn fw-bold w-100 d-flex align-items-center justify-content-center gap-2 ${isEditMode ? 'btn-warning text-dark' : 'btn-danger'}`}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        style={{ opacity: canSubmit ? 1 : 0.45, padding: '9px 0' }}
                    >
                        {isEditMode
                            ? <><i className="bi bi-pencil-fill" style={{ fontSize: 12 }} /> Update Filter</>
                            : <><PlusOutlined /> Add Filter</>
                        }
                    </button>

                    {isEditMode && (
                        <button className="btn btn-sm btn-link text-muted w-100 mt-2" style={{ fontSize: 12 }}
                                onClick={() => { setNewCol(null); setNewOp(null); setNewVal(''); }}>
                            Cancel edit
                        </button>
                    )}
                </div>
            </Modal>
        </>
    );
};

// ─── ColumnSelectorDropdown component ────────────────────────────────────────
// Extracted so it can derive checkAll / indeterminate state from columnControls.

const ColumnSelectorDropdown = ({ columnControls }) => {
    const allKeys = columnControls.toggleableColumns.map(c => c.key);
    // Only keep keys that are actually toggleable — this works for both Products
    // (fixed: index / productDetails / details) and Order Items (fixed: userDetails / info).
    const allKeysSet = new Set(allKeys);
    const checkedKeys = columnControls.visibleColumns.filter(key => allKeysSet.has(key));

    const checkAll      = checkedKeys.length === allKeys.length;
    const indeterminate = checkedKeys.length > 0 && checkedKeys.length < allKeys.length;

    const onCheckAllChange = (e) => {
        columnControls.handleColumnToggle(e.target.checked ? allKeys : []);
    };

    return (
        <div className="card shadow p-4" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '220px' }}>
            <div className="fw-bold mb-3 border-bottom pb-2">Display Columns</div>
            <div className="mb-2 pb-2 border-bottom">
                <Checkbox
                    indeterminate={indeterminate}
                    checked={checkAll}
                    onChange={onCheckAllChange}
                >
                    <span className="fw-semibold">Select All</span>
                </Checkbox>
            </div>
            <Checkbox.Group
                options={columnControls.toggleableColumns.map(c => ({ label: c.title, value: c.key }))}
                value={checkedKeys}
                onChange={columnControls.handleColumnToggle}
                className="d-flex flex-column gap-2"
            />
        </div>
    );
};

// ─── ItemMaster component ─────────────────────────────────────────────────────

const ItemMaster = ({
                        userData,
                        selectedMarketplace,
                        selectedCurrencySign,
                        selectedTileData,
                        onFilteredAsinsChange,  // ← forwarded from ChartComponent → Products
                    }) => {
    const [activeView, setActiveView]         = useState('products');
    const [dropdownOpen, setDropdownOpen]     = useState(false);
    const [groupByOpen, setGroupByOpen]       = useState(false);
    const [groupBy, setGroupBy]               = useState('');
    const dropdownRef                         = useRef();
    const groupByRef                          = useRef();
    const [exporting, setExporting]           = useState(false);
    const [columnControls, setColumnControls] = useState(null);
    const [orderItemColumnControls, setOrderItemColumnControls] = useState(null);
    const [filterControls, setFilterControls] = useState(null);
    const navigate                            = useNavigate();
    const [presets, setPresets]               = useState([]);
    const [selectedPresetKeys, setSelectedPresetKeys] = useState(new Set());
    const [presetsLoading, setPresetsLoading] = useState(false);
    const [filterDropdownOptions, setFilterDropdownOptions] = useState({});
    const justAppliedPresetsRef  = useRef(new Set());
    const presetsInflightRef     = useRef(false);

    // Add this state near the other useState declarations (around line 440-450)
    const [isEasyShipOrder, setIsEasyShipOrder] = useState(null); // null = no filter, 0 or 1 for filtering

    // ─── Fetch saved presets ────────────────────────────────────────────────
    const fetchPresets = useCallback(async () => {
        if (!userData?.token || presetsInflightRef.current) return;
        presetsInflightRef.current = true;
        setPresetsLoading(true);
        try {
            const response = await MakeApiCall(
                'dashboard/tiles/get-saved-presets',
                'get', null, true, {}, userData?.token
            );
            if (response?.status) {
                // Extract filter dropdown options if the presets endpoint provides them
                if (response?.data?.filter_dropdown_options) {
                    setFilterDropdownOptions(response.data.filter_dropdown_options);
                    try { localStorage.setItem('sv_filter_dropdown_options', JSON.stringify(response.data.filter_dropdown_options)); } catch {}
                }
                const systemPresets = Object.entries(response?.data?.system_presets || {}).map(([key, preset]) => ({
                    key,
                    type:       'system',
                    label:      preset.label,
                    conditions: [{ attribute: preset.attributes, operator: preset.operator, value: preset.value }],
                }));
                const customPresets = Object.entries(response?.data?.custom_presets || {}).map(([key, preset]) => ({
                    key,
                    type:       'custom',
                    id:         key.replace(/^custom_/, ''),
                    label:      preset.label,
                    conditions: (preset.conditions || []).map(c => ({ attribute: c.attribute, operator: c.operator, value: c.value })),
                }));
                setPresets([...systemPresets, ...customPresets]);
            }
        } catch {
            // non-critical — presets simply won't appear
        } finally {
            setPresetsLoading(false);
            presetsInflightRef.current = false;
        }
    }, [userData?.token]);

    useEffect(() => {
        fetchPresets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.token]);

    // ─── Handle preset click ────────────────────────────────────────────────
    const handlePresetClick = useCallback((preset) => {
        if (!filterControls) return;
        const { filterableColumns, activeFilters, addFilter, removeFilter } = filterControls;
    
        // ── Deselect (toggle off) ────────────────────────────────────────────
        if (selectedPresetKeys.has(preset.key)) {
            preset.conditions.forEach(cond => {
                const existing = activeFilters.find(f => f.id === `preset_${preset.key}_${cond.attribute}`);
                if (existing) removeFilter(existing.id);
            });
            setSelectedPresetKeys(prev => {
                const next = new Set(prev);
                next.delete(preset.key);
                return next;
            });
            return;
        }
    
        // ── If system preset: deselect any other active system preset first ──
        if (preset.type === 'system') {
            const activeSystemKeys = [...selectedPresetKeys].filter(key => {
                const p = presets.find(p => p.key === key);
                return p?.type === 'system';
            });
    
            activeSystemKeys.forEach(activeKey => {
                const activePreset = presets.find(p => p.key === activeKey);
                if (!activePreset) return;
    
                // Remove its filters
                activePreset.conditions.forEach(cond => {
                    const existing = activeFilters.find(f => f.id === `preset_${activeKey}_${cond.attribute}`);
                    if (existing) removeFilter(existing.id);
                });
    
                // Deselect it
                setSelectedPresetKeys(prev => {
                    const next = new Set(prev);
                    next.delete(activeKey);
                    return next;
                });
            });
        }
    
        // ── Apply new preset's conditions ────────────────────────────────────
        preset.conditions.forEach(cond => {
            const internalOp = API_OPERATOR_MAP[cond.operator] || 'eq';
            const colDef     = filterableColumns.find(c => c.key === cond.attribute);
    
            const existingOnCol = activeFilters.find(f => f.column === cond.attribute);
            if (existingOnCol) removeFilter(existingOnCol.id);
    
            justAppliedPresetsRef.current.add(preset.key);
            addFilter({
                id:          `preset_${preset.key}_${cond.attribute}`,
                column:      cond.attribute,
                columnLabel: colDef?.label || cond.attribute,
                type:        colDef?.type  || 'numeric',
                operator:    internalOp,
                value:       cond.value,
            });
        });
    
        setSelectedPresetKeys(prev => new Set(prev).add(preset.key));
    }, [filterControls, selectedPresetKeys, presets]);

    // ─── Save current active filters as a new custom preset ────────────────
    const handleSavePreset = useCallback(async (name) => {
        if (!filterControls) return;
        const conditions = filterControls.activeFilters.map(f => ({
            attribute: f.column,
            operator:  INTERNAL_OPERATOR_MAP[f.operator] || f.operator,
            value:     f.value,
        }));
        message.loading({ content: 'Saving preset…', key: 'preset_save' });
        const response = await MakeApiCall(
            'dashboard/tiles/column-filter-presets',
            'post', { name, conditions }, true, {}, userData?.token
        );
        if (response?.status) {
            message.success({ content: 'Preset saved!', key: 'preset_save' });
            fetchPresets();
        } else {
            message.error({ content: response?.message || 'Failed to save preset', key: 'preset_save' });
        }
    }, [filterControls, userData?.token, fetchPresets]);

    // ─── Rename an existing custom preset ──────────────────────────────────
    const handleUpdatePreset = useCallback(async (id, name) => {
        message.loading({ content: 'Updating preset…', key: 'preset_update' });
        const response = await MakeApiCall(
            `dashboard/tiles/column-filter-presets/${id}`,
            'post', { name }, true, {}, userData?.token
        );
        if (response?.status) {
            message.success({ content: 'Preset renamed!', key: 'preset_update' });
            fetchPresets();
        } else {
            message.error({ content: response?.message || 'Failed to update preset', key: 'preset_update' });
        }
    }, [userData?.token, fetchPresets]);

    // ─── Delete a custom preset ─────────────────────────────────────────────
    const handleDeletePreset = useCallback(async (id, key) => {
        message.loading({ content: 'Deleting preset…', key: 'preset_delete' });
        const response = await MakeApiCall(
            `dashboard/tiles/column-filter-presets/${id}`,
            'delete', null, true, {}, userData?.token
        );
        if (response?.status) {
            message.success({ content: 'Preset deleted!', key: 'preset_delete' });
            setSelectedPresetKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
            fetchPresets();
        } else {
            message.error({ content: response?.message || 'Failed to delete preset', key: 'preset_delete' });
        }
    }, [userData?.token, fetchPresets]);

    useEffect(() => {
        if (selectedPresetKeys.size === 0 || !filterControls) return;

        // Drain any just-applied keys so we don't immediately deselect them
        if (justAppliedPresetsRef.current.size > 0) {
            justAppliedPresetsRef.current = new Set();
            return;
        }

        const keysToRemove = [];
        selectedPresetKeys.forEach(key => {
            // A preset is deselected when none of its filters remain
            const hasAnyFilter = filterControls.activeFilters.some(f => f.id.startsWith(`preset_${key}_`));
            if (!hasAnyFilter) keysToRemove.push(key);
        });
        if (keysToRemove.length > 0) {
            setSelectedPresetKeys(prev => {
                const next = new Set(prev);
                keysToRemove.forEach(k => next.delete(k));
                return next;
            });
        }
    }, [filterControls?.activeFilters, selectedPresetKeys]);

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const endpoint = activeView === 'orderItems'
                ? "dashboard/export/pl-order-wise"
                : "dashboard/export/pl-asin-wise";

            const response = await MakeApiCall(
                endpoint,
                "post",
                {
                    date_from:      selectedTileData?.start_date,
                    date_to:        selectedTileData?.end_date,
                    marketplace_id: selectedMarketplace?.id,
                },
                true, {}, userData?.token
            );
            if (response?.status) {
                const fileId = response.data?.file_id ?? response.data?.export_file_id ?? "";
                const key = `export-notif-${Date.now()}`;
                notification.success({
                    key,
                    message: "Export started",
                    description: (
                        <span>
                            {fileId && <><b>{fileId}</b> — </>}
                            {response.message ?? "Your file is being prepared."}{" "}
                            <a
                                href="/settings/file-manager?tab=export"
                                style={{ color: "#f45b68", fontWeight: 600 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    notification.destroy(key);
                                    navigate("/settings/file-manager?tab=export");
                                }}
                            >
                                Go to Import & Export
                            </a>
                        </span>
                    ),
                    duration: 0,
                    placement: "top",
                    style: { width: "max-content" }
                });
            } else {
                message.error(response?.message || "Export failed. Please try again.");
            }
        } catch (err) {
            console.error("Dashboard export error:", err);
            message.error("Something went wrong while starting the export.");
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
            if (groupByRef.current && !groupByRef.current.contains(event.target)) {
                setGroupByOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDropdown = () => setDropdownOpen(prev => !prev);
    const selectView     = view => { setActiveView(view); setDropdownOpen(false); };


    const handleFiltersReady = useCallback(controls => {
        setFilterControls(controls);
    }, []);

    return (
        <Wrapper>
            <div className="card card-xxl-stretch mb-5 mb-xl-8 mt-4 border-0 shadow-sm rounded-4">

                <div className="card-header bg-transparent pb-0 px-5 d-flex flex-wrap align-items-center gap-2">

                    <div className="dropdown position-relative " ref={dropdownRef}>
                        <button
                            className="btn btn-danger btn-lg fw-semibold px-2 px-lg-5 py-2 rounded-1 shadow-sm border d-flex align-items-center fs-7"
                            onClick={toggleDropdown}
                        >
                            {activeView === 'products' ? <i className="bi bi-box-seam me-2 fs-4"></i> : <i className="bi bi-cart3 me-2 fs-4"></i>}
                            {activeView === 'products' ? 'Products' : 'Order Items'}
                            <i className="ms-2 bi bi-caret-down-fill fs-7"></i>
                        </button>
                        <ul className={`dropdown-menu ${dropdownOpen ? "show" : ""}`} style={{ width: "100%", minWidth: "unset", boxSizing: "border-box" }}>
                            <li>
                                <button className={`dropdown-item ${activeView === 'products' ? 'active' : ''}`} onClick={() => selectView('products')}
                                        style={{ backgroundColor: activeView === 'products' ? '#dc3545' : '#f8f9fa', color: activeView === 'products' ? '#fff' : '#000', fontWeight: 500, padding: "10px 15px", borderRadius: 4, marginBottom: 5, border: "1px solid #dee2e6", textAlign: "left" }}>
                                    <i className="bi bi-box-seam me-2 fs-4"></i>Products
                                </button>
                            </li>
                            <li>
                                <button className={`dropdown-item ${activeView === 'orderItems' ? 'active' : ''}`} onClick={() => selectView('orderItems')}
                                        style={{ backgroundColor: activeView === 'orderItems' ? '#dc3545' : '#f8f9fa', color: activeView === 'orderItems' ? '#fff' : '#000', fontWeight: 500, padding: "10px 15px", borderRadius: 4, border: "1px solid #dee2e6", textAlign: "left" }}>
                                    <i className="bi bi-cart3 me-2 fs-4"></i>Order Items
                                </button>
                            </li>
                        </ul>
                    </div>

                    {activeView === 'products' && (
                        <div className="dropdown position-relative" ref={groupByRef}>
                            <button
                                className="btn btn-danger btn-lg fw-semibold px-2 px-lg-5 py-2 rounded-1 shadow-sm border d-flex align-items-center fs-7"
                                style={{ whiteSpace: 'nowrap' }}
                                onClick={() => setGroupByOpen(prev => !prev)}
                            >
                                {groupBy === 'parent'
                                    ? <i className="bi bi-diagram-2 me-2 fs-4" />
                                    : groupBy === 'tag'
                                        ? <i className="bi bi-tag me-2 fs-4" />
                                        : <i className="bi bi-collection me-2 fs-4" />
                                }
                                {groupBy === 'parent' ? 'Group By Parent ASIN' : groupBy === 'tag' ? 'Group By Tag' : 'Group By'}
                                {groupBy ? (
                                    <span
                                        onClick={e => { e.stopPropagation(); setGroupBy(''); setGroupByOpen(false); }}
                                        title="Clear group"
                                        style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 16, height: 16, borderRadius: '50%', background: 'rgba(255,255,255,0.3)',
                                            fontSize: 10, fontWeight: 700, lineHeight: 1, cursor: 'pointer',
                                        }}
                                    >✕</span>
                                ) : (
                                    <i className="ms-2 bi bi-caret-down-fill fs-7" />
                                )}
                            </button>
                            <ul className={`dropdown-menu ${groupByOpen ? 'show' : ''}`} style={{ minWidth: '100%' }}>
                                <li>
                                    <button
                                        className={`dropdown-item ${groupBy === 'parent' ? 'active' : ''}`}
                                        onClick={() => { setGroupBy('parent'); setGroupByOpen(false); }}
                                        style={{ backgroundColor: groupBy === 'parent' ? '#dc3545' : '#f8f9fa', color: groupBy === 'parent' ? '#fff' : '#000', fontWeight: 500, padding: '10px 15px', borderRadius: 4, marginBottom: 5, border: '1px solid #dee2e6', textAlign: 'left', whiteSpace: 'nowrap', width: '100%' }}
                                    >
                                        <i className="bi bi-diagram-2 me-2 fs-4" />Parent ASIN
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className={`dropdown-item ${groupBy === 'tag' ? 'active' : ''}`}
                                        onClick={() => { setGroupBy('tag'); setGroupByOpen(false); }}
                                        style={{ backgroundColor: groupBy === 'tag' ? '#dc3545' : '#f8f9fa', color: groupBy === 'tag' ? '#fff' : '#000', fontWeight: 500, padding: '10px 15px', borderRadius: 4, border: '1px solid #dee2e6', textAlign: 'left', whiteSpace: 'nowrap', width: '100%' }}
                                    >
                                        <i className="bi bi-tag me-2 fs-4" />Tag
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                        <div className="d-flex align-items-center ms-auto gap-2">

                            {activeView === 'products' && (
                                <FilterPanel
                                    filterControls={filterControls}
                                    filterDropdownOptions={filterDropdownOptions}
                                    presets={presets}
                                    selectedPresetKeys={selectedPresetKeys}
                                    onPresetClick={handlePresetClick}
                                    presetsLoading={presetsLoading}
                                    onSavePreset={handleSavePreset}
                                    onUpdatePreset={handleUpdatePreset}
                                    onDeletePreset={handleDeletePreset}
                                />
                            )}

                            {activeView === 'products' && columnControls && (
                                <Dropdown
                                    trigger={['click']}
                                    placement="bottomRight"
                                    overlay={<ColumnSelectorDropdown columnControls={columnControls} />}
                                >
                                    <Button icon={<SettingOutlined />} className="btn btn-danger fs-7 fw-bold d-flex align-items-center" style={{ height: 'auto', border: 'none' }}>
                                        Customize Columns
                                    </Button>
                                </Dropdown>
                            )}

                            {activeView === 'orderItems' && orderItemColumnControls && (
                                <Dropdown
                                    trigger={['click']}
                                    placement="bottomRight"
                                    overlay={<ColumnSelectorDropdown columnControls={orderItemColumnControls} />}
                                >
                                    <Button icon={<SettingOutlined />} className="btn btn-danger fs-7 fw-bold d-flex align-items-center" style={{ height: 'auto', border: 'none' }}>
                                        Customize Columns
                                    </Button>
                                </Dropdown>
                            )}

                            {/*{activeView === 'orderItems' && (*/}
                            {/*    <Select*/}
                            {/*        placeholder="Easy Ship Orders"*/}
                            {/*        style={{ width: 200 }}*/}
                            {/*        size="middle"*/}
                            {/*        allowClear*/}
                            {/*        value={isEasyShipOrder}*/}
                            {/*        onChange={setIsEasyShipOrder}*/}
                            {/*        className='mt-n3'*/}
                            {/*        options={[*/}
                            {/*            { value: 1, label: 'Easy Ship Orders' },*/}
                            {/*            { value: 0, label: 'Non Easy Ship Orders' },*/}
                            {/*        ]}*/}
                            {/*    />*/}
                            {/*)}*/}

                            <button className="btn btn-danger fs-7 fw-bold d-flex align-items-center px-3 px-md-5" onClick={handleExport} disabled={exporting}>
                                {exporting ? <><Spin size="small" className="me-2" /> Exporting...</> : <>Export <i className="ms-1 bi bi-upload fs-4" /></>}
                            </button>

                        </div>
                </div>

                <div className="card-body pt-4 px-5">
                    {activeView === 'products' && (
                        <Products
                            selectedTileData={selectedTileData}
                            userData={userData}
                            selectedCurrencySign={selectedCurrencySign}
                            selectedMarketplace={selectedMarketplace}
                            groupBy={groupBy}
                            onColumnsReady={setColumnControls}
                            onFiltersReady={handleFiltersReady}
                            onFilteredAsinsChange={onFilteredAsinsChange}  // ← forwarded
                        />
                    )}
                    {activeView === 'orderItems' && (
                        <OrderItems
                            selectedTileData={selectedTileData}
                            userData={userData}
                            selectedCurrencySign={selectedCurrencySign}
                            selectedMarketplace={selectedMarketplace}
                            isEasyShipOrder={isEasyShipOrder}
                            onColumnsReady={setOrderItemColumnControls}
                        />
                    )}
                </div>

            </div>
        </Wrapper>
    );
};

export default ItemMaster;